import React from 'react';
import { Link } from 'react-router';
import {
  COMMISSIONER_ACCESS_JOIN_HEADLINE,
  type CommissionerAccessPresentation,
} from '../commissionerAccess';
import { lscPath } from '../routing';

interface CommissionerUpgradePanelProps {
  presentation: CommissionerAccessPresentation;
  /** Optional override for panel test id / variant. */
  testId?: string;
}

const CommissionerUpgradePanel: React.FC<CommissionerUpgradePanelProps> = ({
  presentation,
  testId = 'commissioner-upgrade-panel',
}) => {
  const joinCtas = presentation.secondaryCtas.filter((cta) =>
    ['join', 'leagues'].includes(cta.code)
  );
  const otherSecondary = presentation.secondaryCtas.filter(
    (cta) => !['join', 'leagues'].includes(cta.code)
  );

  return (
    <div className="space-y-6 max-w-lg" data-testid={testId}>
      <div>
        <h1 className="text-2xl font-bold text-white mb-2">{presentation.headline}</h1>
        {presentation.detailLines.map((line) => (
          <p key={line} className="text-sm text-secondary-300 mt-2">
            {line}
          </p>
        ))}
      </div>

      <div className="card space-y-4" data-testid={`${testId}-actions`}>
        {presentation.primaryCta ? (
          presentation.primaryCta.href ? (
            <Link
              to={lscPath(presentation.primaryCta.href)}
              className="btn-primary inline-flex min-h-11 items-center"
              data-testid={`${testId}-primary-cta`}
            >
              {presentation.primaryCta.label}
            </Link>
          ) : (
            <p className="text-sm text-secondary-400">{presentation.primaryCta.label}</p>
          )
        ) : null}

        {otherSecondary.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {otherSecondary.map((cta) =>
              cta.href ? (
                <Link
                  key={cta.code}
                  to={lscPath(cta.href)}
                  className="btn-secondary text-sm"
                  data-testid={`${testId}-secondary-${cta.code}`}
                >
                  {cta.label}
                </Link>
              ) : null
            )}
          </div>
        ) : null}
      </div>

      {presentation.showJoinPath ? (
        <div
          className="rounded-lg border border-secondary-700 bg-secondary-900/40 px-4 py-4 space-y-3"
          data-testid={`${testId}-join-path`}
        >
          <h2 className="text-sm font-medium text-white">{COMMISSIONER_ACCESS_JOIN_HEADLINE}</h2>
          <p className="text-xs text-secondary-400">
            Members join with an invite code from their commissioner. League creation is not required.
          </p>
          <div className="flex flex-wrap gap-2">
            {joinCtas.map((cta) =>
              cta.href ? (
                <Link
                  key={cta.code}
                  to={lscPath(cta.href)}
                  className="btn-secondary text-sm"
                  data-testid={`${testId}-join-${cta.code}`}
                >
                  {cta.label}
                </Link>
              ) : null
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default CommissionerUpgradePanel;
